import { ReactElement } from 'react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';

import {
  COMP_ENTRIES,
  COMP_HANDICAP_VALUE_TYPES,
  GROUP_CALCS,
  CompEntry,
  CompHandicapValueType,
  GroupCalc,
  IndexBracket,
} from '../../types/Competition';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { formatEnumLabel } from '../../utils/utils';

interface IndexBracketListEditorProps {
  brackets: IndexBracket[];
  /** Gates the "Combine" (groupCalc) column — irrelevant for a SINGLE entry, where there's only one player to reduce. */
  entry: CompEntry | undefined;
  /** Whether to show the "Value" (CompHandicapValueType) column. Default true. Callers with no
   *  course/tee context to compute PLAYING_HANDICAP/COURSE_HANDICAP from (e.g. league-level
   *  brackets, which span many courses across a season) should pass false — brackets then always
   *  classify on raw GHIN index, matching the field's own default. */
  showTypeDropdown?: boolean;
  onChange: (brackets: IndexBracket[]) => void;
}

const HANDICAP_VALUE_TYPE_LABELS: Record<CompHandicapValueType, string> = {
  [COMP_HANDICAP_VALUE_TYPES.PLAYING_HANDICAP]: HANDICAP_LABELS.PLAYING_HANDICAP,
  [COMP_HANDICAP_VALUE_TYPES.COURSE_HANDICAP]: HANDICAP_LABELS.COURSE_HANDICAP,
  [COMP_HANDICAP_VALUE_TYPES.GHIN_INDEX]: HANDICAP_LABELS.INDEX_SHORT,
};

export default function IndexBracketListEditor({
  brackets,
  entry,
  showTypeDropdown = true,
  onChange,
}: IndexBracketListEditorProps): ReactElement {
  const handleBracketChange = (index: number, changes: Partial<IndexBracket>) => {
    const updated = [...brackets];
    updated[index] = { ...updated[index]!, ...changes };
    onChange(updated);
  };

  const addBracket = () => {
    const lastHigh = brackets.length > 0 ? brackets[brackets.length - 1]!.high : -0.1;
    onChange([...brackets, { low: lastHigh + 0.1, high: 54 }]);
  };

  const removeBracket = (index: number) => {
    onChange(brackets.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={1}>
      {brackets.map((bracket, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            label="Label"
            size="small"
            sx={{ flexGrow: 1 }}
            value={bracket.label ?? ''}
            onChange={({ target }) => handleBracketChange(i, { label: target.value || undefined })}
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
          {showTypeDropdown && (
            <TextField
              select
              label="Value"
              size="small"
              sx={{ width: '11rem' }}
              value={bracket.type ?? COMP_HANDICAP_VALUE_TYPES.GHIN_INDEX}
              onChange={({ target }) => handleBracketChange(i, { type: target.value as CompHandicapValueType })}
            >
              {Object.values(COMP_HANDICAP_VALUE_TYPES).map((v) => (
                <MenuItem key={v} value={v}>{HANDICAP_VALUE_TYPE_LABELS[v]}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Low"
            type="number"
            size="small"
            sx={{ width: '6rem' }}
            slotProps={{ htmlInput: { min: -10, max: 54, step: 0.1 } }}
            value={bracket.low}
            onChange={({ target }) => {
              const parsed = parseFloat(target.value);
              if (!isNaN(parsed)) handleBracketChange(i, { low: parsed });
            }}
          />
          <TextField
            label="High"
            type="number"
            size="small"
            sx={{ width: '6rem' }}
            slotProps={{ htmlInput: { min: -10, max: 54, step: 0.1 } }}
            value={bracket.high}
            onChange={({ target }) => {
              const parsed = parseFloat(target.value);
              if (!isNaN(parsed)) handleBracketChange(i, { high: parsed });
            }}
          />
          {entry && entry !== COMP_ENTRIES.SINGLE && (
            <TextField
              select
              label="Combine"
              size="small"
              sx={{ width: '9rem' }}
              value={bracket.groupCalc ?? GROUP_CALCS.AVG}
              onChange={({ target }) => handleBracketChange(i, { groupCalc: target.value as GroupCalc })}
            >
              {Object.values(GROUP_CALCS).map((v) => (
                <MenuItem key={v} value={v}>{formatEnumLabel(v)}</MenuItem>
              ))}
            </TextField>
          )}
          <IconButton
            size="small"
            aria-label="Remove bracket"
            onClick={() => removeBracket(i)}
            sx={{ alignSelf: 'center' }}
          >
            <RemoveCircleOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBracket}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add bracket
      </Button>
    </Stack>
  );
}
